import {
  isPublicGameWithTeams,
  type PublicGame,
} from '~/domains/contest/domains/services/interfaces/propsService/api/game';

import type {
  PublicMarketGroup,
  PublicPredictionMarket,
  PublicMarketGamelineSpread,
  PublicMarketGamelineTotal,
  PublicMarketGamelineMoneyline,
  MarketSubCategory,
} from '../services/interfaces/market.types';
import { isMoneylineTwoWay } from '../services/interfaces/market.types';
import type {
  BettingLine,
  SpreadMarketData,
  TotalMarketData,
  MoneylineMarketData,
} from '../components/MarketCard/types';

export type NormalizedMarketGroup = {
  id: string;
  eventId: string;
  eventStart: number;
  subCategory: MarketSubCategory;

  homeTeamId: string;
  awayTeamId: string;

  homeTeamLine: BettingLine;
  awayTeamLine: BettingLine;

  spreadMarketId: string | null;
  totalMarketId: string | null;
  homeMoneylineMarketId: string | null;
  awayMoneylineMarketId: string | null;

  markets: PublicPredictionMarket[];
  alternateMarkets: PublicPredictionMarket[];
};

export function extractBettingLinesForGroup(
  markets: PublicPredictionMarket[],
  homeTeamId: string,
  awayTeamId: string
): {
  homeTeamLine: BettingLine;
  awayTeamLine: BettingLine;
  spreadMarketId: string | null;
  totalMarketId: string | null;
  homeMoneylineMarketId: string | null;
  awayMoneylineMarketId: string | null;
} {
  const homeTeamLine: BettingLine = {};
  const awayTeamLine: BettingLine = {};
  let spreadMarketId: string | null = null;
  let totalMarketId: string | null = null;
  let homeMoneylineMarketId: string | null = null;
  let awayMoneylineMarketId: string | null = null;

  const spreadMarket = markets.find((m): m is PublicMarketGamelineSpread => m.type === 'spread');
  if (spreadMarket) {
    spreadMarketId = spreadMarket.id;
    const homeSpread: SpreadMarketData = {
      marketId: spreadMarket.id,
      lineDisplay:
        spreadMarket.yes_team_id === homeTeamId
          ? spreadMarket.yes_line_display
          : spreadMarket.no_line_display,
      price:
        spreadMarket.yes_team_id === homeTeamId ? spreadMarket.price_yes : spreadMarket.price_no,
    };
    const awaySpread: SpreadMarketData = {
      marketId: spreadMarket.id,
      lineDisplay:
        spreadMarket.yes_team_id === awayTeamId
          ? spreadMarket.yes_line_display
          : spreadMarket.no_line_display,
      price:
        spreadMarket.yes_team_id === awayTeamId ? spreadMarket.price_yes : spreadMarket.price_no,
    };
    homeTeamLine.spread = homeSpread;
    awayTeamLine.spread = awaySpread;
  }

  const totalMarket = markets.find((m): m is PublicMarketGamelineTotal => m.type === 'total');
  if (totalMarket) {
    totalMarketId = totalMarket.id;
    const overTotal: TotalMarketData = {
      marketId: totalMarket.id,
      lineDisplay: totalMarket.yes_line_display,
      price: totalMarket.price_yes,
    };
    const underTotal: TotalMarketData = {
      marketId: totalMarket.id,
      lineDisplay: totalMarket.no_line_display,
      price: totalMarket.price_no,
    };
    awayTeamLine.total = overTotal;
    homeTeamLine.total = underTotal;
  }

  const moneylineMarkets = markets.filter(
    (m): m is PublicMarketGamelineMoneyline => m.type === 'moneyline'
  );
  for (const moneyline of moneylineMarkets) {
    if (isMoneylineTwoWay(moneyline)) {
      const isHomeYes = moneyline.yes_team_id === homeTeamId;
      homeTeamLine.moneyline = {
        marketId: moneyline.id,
        price: isHomeYes ? moneyline.price_yes : moneyline.price_no,
        side: isHomeYes ? 'yes' : 'no',
      };
      awayTeamLine.moneyline = {
        marketId: moneyline.id,
        price: isHomeYes ? moneyline.price_no : moneyline.price_yes,
        side: isHomeYes ? 'no' : 'yes',
      };
      homeMoneylineMarketId = moneyline.id;
      awayMoneylineMarketId = moneyline.id;
    } else {
      const moneylineData: MoneylineMarketData = {
        marketId: moneyline.id,
        price: moneyline.price_yes,
        side: 'yes',
      };
      if (moneyline.team_id === homeTeamId) {
        homeTeamLine.moneyline = moneylineData;
        homeMoneylineMarketId = moneyline.id;
      } else if (moneyline.team_id === awayTeamId) {
        awayTeamLine.moneyline = moneylineData;
        awayMoneylineMarketId = moneyline.id;
      }
    }
  }

  return {
    homeTeamLine,
    awayTeamLine,
    spreadMarketId,
    totalMarketId,
    homeMoneylineMarketId,
    awayMoneylineMarketId,
  };
}

export function normalizeMarketGroup(
  marketGroup: PublicMarketGroup,
  eventsById: Map<string, PublicGame>
): NormalizedMarketGroup | null {
  if (marketGroup.type !== 'gameline') return null;

  const event = eventsById.get(marketGroup.event_id);
  if (!event || !isPublicGameWithTeams(event)) return null;

  const homeTeamId = event.home.id;
  const awayTeamId = event.away.id;

  const {
    homeTeamLine,
    awayTeamLine,
    spreadMarketId,
    totalMarketId,
    homeMoneylineMarketId,
    awayMoneylineMarketId,
  } = extractBettingLinesForGroup(marketGroup.markets, homeTeamId, awayTeamId);

  return {
    id: marketGroup.id,
    eventId: marketGroup.event_id,
    eventStart: marketGroup.event_start ?? event.start_date,
    subCategory: marketGroup.sub_category,
    homeTeamId,
    awayTeamId,
    homeTeamLine,
    awayTeamLine,
    spreadMarketId,
    totalMarketId,
    homeMoneylineMarketId,
    awayMoneylineMarketId,
    markets: marketGroup.markets,
    alternateMarkets: marketGroup.alternate_markets ?? [],
  };
}

export function mergeAndNormalizeMarketGroups(
  existingRaw: Map<string, PublicMarketGroup>,
  existingNormalized: Map<string, NormalizedMarketGroup>,
  existingMarketIdToGroupId: Map<string, string>,
  newMarketGroups: PublicMarketGroup[],
  eventsById: Map<string, PublicGame>
): {
  rawMap: Map<string, PublicMarketGroup>;
  normalizedMap: Map<string, NormalizedMarketGroup>;
  marketIdToGroupIdMap: Map<string, string>;
} {
  const rawMap = new Map(existingRaw);
  const normalizedMap = new Map(existingNormalized);
  const marketIdToGroupIdMap = new Map(existingMarketIdToGroupId);

  for (const mg of newMarketGroups) {
    rawMap.set(mg.id, mg);
    const normalized = normalizeMarketGroup(mg, eventsById);
    if (normalized) {
      normalizedMap.set(mg.id, normalized);
    }

    for (const market of mg.markets) {
      marketIdToGroupIdMap.set(market.id, mg.id);
    }
    for (const market of mg.alternate_markets ?? []) {
      marketIdToGroupIdMap.set(market.id, mg.id);
    }
  }

  return { rawMap, normalizedMap, marketIdToGroupIdMap };
}
